import type { ChipIntent } from '@/components/common/chip';
import { formatInAppTz } from '@/lib/date';

import type { PaymentFor, PaymentMethod, PaymentStatus } from './types';

/** Table Date & Time: the UTC wire instant on the application (Eastern) clock (ADO 120986). */
export function formatPaymentDateTime(iso: string): string {
  return formatInAppTz(iso, 'D MMM YYYY | hh:mm A');
}

/** Timeline event timestamp (comma separator per Figma). */
export function formatTimelineDateTime(iso: string): string {
  return formatInAppTz(iso, 'D MMM YYYY, hh:mm A');
}

/** The Eastern calendar day of an instant (the timeline header's Order Date). */
export function formatDate(iso: string): string {
  return formatInAppTz(iso, 'D MMM YYYY');
}

/** Masked payment-method display. Card → "Credit Card (Visa - 1111)"; net banking → "Net Banking (Chase Bank)". */
export function formatPaymentMethod(m: PaymentMethod): string {
  if (m.type === 'card') return `Credit Card (${m.brand ?? '—'} - ${m.last4 ?? '····'})`;
  return `Net Banking (${m.bankName ?? '—'})`;
}

export const PAYMENT_FOR_LABEL: Record<PaymentFor, string> = {
  order: 'Order',
  subscription: 'Subscription',
  installation: 'Installation',
  refund: 'Refund',
};

/** Status label + chip intent, keyed on the wire enum. `capture_failed` shares Failed's red
 *  (distinct label); exact per-status hue fidelity (purple/olive/peach) is a deferred polish
 *  (ADR-0017 / plan Task 2b-polish) — the tracer bullet reuses the existing six intents. */
export const PAYMENT_STATUS_CHIP: Record<PaymentStatus, { label: string; intent: ChipIntent }> = {
  pending: { label: 'Pending', intent: 'review' },
  success: { label: 'Success', intent: 'approved' },
  failed: { label: 'Failed', intent: 'attention' },
  on_hold: { label: 'On Hold', intent: 'published' },
  debited: { label: 'Debited', intent: 'approved' },
  released: { label: 'Released', intent: 'neutral' },
  capture_failed: { label: 'Capture Failed', intent: 'attention' },
  refund_initiated: { label: 'Refund Initiated', intent: 'wip' },
  refunded: { label: 'Refunded', intent: 'wip' },
  disputed: { label: 'Disputed', intent: 'attention' },
};
